import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ax/Button";
import { useI18n } from "@/i18n";
import { readPublicPlans } from "@/lib/publicPlans";
import styles from "./LandingVisual.module.css";

const FEATURES = ["landing.pricing.feature1", "landing.pricing.feature2", "landing.pricing.feature3"];
const rupiah = (value) => `Rp${value.toLocaleString("id-ID")}`;

export function LandingPricing({ onStartTrial }) {
  const { t } = useI18n();
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ status: "loading", plans: null });

  useEffect(() => {
    const controller = new AbortController();
    setResult({ status: "loading", plans: null });
    readPublicPlans(controller.signal).then((plans) => {
      if (!controller.signal.aborted) setResult({ status: plans ? "success" : "empty", plans });
    }).catch(() => {
      if (!controller.signal.aborted) setResult({ status: "error", plans: null });
    });
    return () => controller.abort();
  }, [attempt]);

  const { status, plans } = result;
  return (
    <section id="harga" className={styles.pricing} aria-labelledby="landing-pricing-title" data-testid="landing-pricing" data-state={status}>
      <div className={styles.container}>
        <div className={styles.pricingHeading}>
          <p className={styles.eyebrow}>{t("landing.pricing.eyebrow")}</p>
          <h2 id="landing-pricing-title">{t("landing.pricing.title")}</h2>
          <p className={styles.lede}>{t("landing.pricing.lede")}</p>
        </div>
        {status !== "success" && <div className={styles.priceStatus} data-testid="landing-price-state">
          <p role={status === "error" ? "alert" : "status"}>{t(`landing.pricing.${status}`)}</p>
          {(status === "error" || status === "empty") && <Button variant="ghost" onClick={() => setAttempt((value) => value + 1)} data-testid="landing-price-retry">
            {t("landing.pricing.retry")}
          </Button>}
        </div>}
        <div className={styles.priceGrid} aria-busy={status === "loading"}>
          {["monthly", "annual"].map((period) => {
            const annual = period === "annual";
            return <article key={period} className={`${styles.priceCard} ${annual ? styles.priceAnnual : ""}`} aria-labelledby={`price-${period}-title`} data-testid={`landing-price-${period}`}>
              <div className={styles.priceCardTop}>
                <h3 id={`price-${period}-title`}>{t(`landing.pricing.${period}`)}</h3>
                {annual && plans?.saving > 0 && <span className={styles.priceSaving} data-testid="landing-price-saving">{t("landing.pricing.saving", { price: rupiah(plans.saving) })}</span>}
              </div>
              <p className={styles.priceAmount} data-testid={`landing-price-${period}-amount`}>
                {plans ? rupiah(plans[period]) : <span aria-label={t("landing.pricing.pending")}>—</span>}
                <span>{t(annual ? "landing.pricing.perYear" : "landing.pricing.perMonth")}</span>
              </p>
              {annual && plans && <p className={styles.priceEquivalent}>{t("landing.pricing.equivalent", { price: rupiah(plans.perMonth) })}</p>}
              <p className={styles.priceDescription}>{t(`landing.pricing.${period}Body`)}</p>
              <ul className={styles.priceFeatures}>{FEATURES.map((key) => <li key={key}><Check size={18} aria-hidden="true" /><span>{t(key)}</span></li>)}</ul>
              <Button variant={annual ? "primary" : "secondary"} onClick={onStartTrial} className={styles.priceCta} data-testid={`landing-price-${period}-cta`}>
                {t("landing.visual.trial")}
              </Button>
            </article>;
          })}
        </div>
        <p className={styles.priceAvailability}>{t("landing.pricing.availability")}</p>
      </div>
    </section>
  );
}
